// src/contexts/UserContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { handleJSAPIAccess, handleUserAuth, isCacheExpired, setUserCachedData, getUserCachedData, clearUserCachedData, getUserCacheKey } from '../utils/auth_access_util';
import axios from 'axios';
import { API_CONFIG } from '../config/apiConfig';
import clientConfig from '../config/client_config';
import Cookies from 'js-cookie';

const LJ_TOKEN_KEY = 'lk_token'
// 创建用户上下文
export const UserContext = createContext();

// 上下文提供者组件
export const UserProvider = ({ children }) => {
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isDriver, setIsDriver] = useState(false);
    const [checkingAdmin, setCheckingAdmin] = useState(false);
    const [checkingDriver, setCheckingDriver] = useState(false);

    // 获取用户详细信息
    const fetchUserDetailInfo = async (accessToken, openId) => {
        try {
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/feiShu/getUserInfo`, {
                apiToken: API_CONFIG.apiToken,
                userAccessToken: accessToken,
                userId: openId
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.data && response.data.code === 200) {
                let userDetail = response.data.data;
                if (typeof userDetail === 'string') {
                    try {
                        userDetail = JSON.parse(userDetail);
                    } catch (error) {
                        console.error('解析用户信息失败:', error);
                        throw new Error('用户信息格式错误');
                    }
                }
                let userInfo = userDetail.user || userDetail;
                // 确保 userInfo 包含 avatar_url 字段
                if (!userInfo.avatar_url) {
                    // 从 avatar 对象中提取头像 URL
                    if (userInfo.avatar && userInfo.avatar.avatar_240) {
                        // 移除头像 URL 中的反引号
                        userInfo.avatar_url = userInfo.avatar.avatar_240.replace(/`/g, '').trim();
                    } else if (userInfo.avatar && userInfo.avatar.avatar_640) {
                        // 移除头像 URL 中的反引号
                        userInfo.avatar_url = userInfo.avatar.avatar_640.replace(/`/g, '').trim();
                    } else if (userInfo.avatar && userInfo.avatar.avatar_72) {
                        // 移除头像 URL 中的反引号
                        userInfo.avatar_url = userInfo.avatar.avatar_72.replace(/`/g, '').trim();
                    } else if (userInfo.avatar && userInfo.avatar.avatar_origin) {
                        // 移除头像 URL 中的反引号
                        userInfo.avatar_url = userInfo.avatar.avatar_origin.replace(/`/g, '').trim();
                    } else {
                        // 如果没有头像 URL，生成一个基于用户名的默认头像
                        userInfo.avatar_url = `https://ui-avatars.com/api/?name=${encodeURIComponent(userInfo.name || '用户')}&background=random&color=fff`;
                    }
                }
                // 处理部门信息 - 兼容多种可能的数据结构
                let deptName = '';
                
                // 尝试从 department_path 获取部门名称
                if (userInfo.department_path && userInfo.department_path.length > 0) {
                    const deptPath = userInfo.department_path[0];
                    console.log('部门路径数据:', deptPath);

                    // 尝试多种可能的字段结构
                    deptName = 
                        deptPath.department_name?.name ||    // 结构1: department_name.name
                        deptPath.name ||                      // 结构2: 直接是name字段
                        deptPath.department_name ||           // 结构3: department_name是字符串
                        deptPath.dept_name ||                 // 结构4: dept_name字段
                        '';

                    console.log('从department_path提取的部门名称:', deptName);
                }
                
                // 如果 department_path 没有获取到，尝试从其他字段获取
                if (!deptName) {
                    deptName = 
                        userInfo.department ||                // 字段: department
                        userInfo.department_name ||           // 字段: department_name
                        userInfo.dept_name ||                 // 字段: dept_name
                        '';
                    
                    if (deptName) {
                        console.log('从其他字段提取的部门名称:', deptName);
                    } else {
                        console.log('没有部门路径信息，部门名称为空');
                    }
                }
                
                userInfo.department_name = deptName;
                return userInfo;
            } else {
                throw new Error('获取用户详细信息失败: ' + (response.data.message || '未知错误'));
            }
        } catch (error) {
            console.error('获取用户详细信息失败:', error);
            throw error;
        }
    };

    // 获取完整的后端URL
    const getOrigin = (apiPort) => {
        let hostname = window.location.hostname;
        let protocol = window.location.protocol;
        
        if (protocol === 'https:') {
            return `${protocol}//${hostname}`;
        }
        
        return `${protocol}//${hostname}:${apiPort}`;
    };

    // 刷新用户 access_token
    const refreshUserAccessToken = async () => {
        try {
            console.log('开始刷新用户 access_token...');
            
            // 检查是否在飞书环境
            const isFeishuEnv = window.h5sdk || window.feishu || window.lark || window.tt;
            if (!isFeishuEnv) {
                console.log('非飞书环境，无法刷新 token');
                return null;
            }

            // 清除旧的 token
            Cookies.remove(LJ_TOKEN_KEY);
            
            // 重新获取授权码
            const authCode = await new Promise((resolve, reject) => {
                window.tt.requestAuthCode({
                    appId: clientConfig.appId,
                    success: (info) => {
                        if (info.code && info.code.length > 0) {
                            resolve(info.code);
                        } else {
                            reject(new Error('获取授权码失败'));
                        }
                    },
                    fail: (error) => {
                        console.error('获取授权码失败:', error);
                        reject(error);
                    }
                });
            });

            // 调用后端接口获取新的 access_token
            const response = await axios.get(
                `${getOrigin(clientConfig.apiPort)}${clientConfig.getUserAccessTokenPath}?code=${authCode}`,
                { withCredentials: true }
            );

            if (response.data && response.data.data) {
                const newAccessToken = response.data.data;
                const userId = newAccessToken.user_id || newAccessToken.open_id;
                
                // 更新 Cookie
                Cookies.set(LJ_TOKEN_KEY, newAccessToken.access_token);
                
                // 更新 userInfo
                const storedUserInfo = localStorage.getItem('userInfo');
                if (storedUserInfo) {
                    const userInfo = JSON.parse(storedUserInfo);
                    userInfo.access_token = newAccessToken.access_token;
                    userInfo.refresh_time = new Date().getTime();
                    localStorage.setItem('userInfo', JSON.stringify(userInfo));
                }
                
                console.log('access_token 刷新成功');
                return newAccessToken;
            } else {
                throw new Error('刷新 access_token 失败');
            }
        } catch (error) {
            console.error('刷新 access_token 失败:', error);
            throw error;
        }
    };

    // 检查 access_token 是否有效
    const checkAccessTokenValidity = (accessToken) => {
        if (!accessToken) return false;
        
        // 检查是否有刷新时间记录
        const storedUserInfo = localStorage.getItem('userInfo');
        if (storedUserInfo) {
            try {
                const userInfo = JSON.parse(storedUserInfo);
                const refreshTime = userInfo.refresh_time || 0;
                const now = new Date().getTime();
                
                // 如果超过1.5小时未刷新，认为需要刷新（token有效期通常为2小时）
                if (now - refreshTime > 90 * 60 * 1000) {
                    console.log('access_token 即将过期，需要刷新');
                    return false;
                }
            } catch (error) {
                console.error('检查 token 有效性失败:', error);
                return false;
            }
        }
        
        return true;
    };

    // 检查是否是管理员
    const checkAdminStatus = async (userMobile, userId) => {
        try {
            // 首先检查localStorage中是否已经存储了管理员状态
            const storedAdminStatus = getUserCachedData(userId, 'isAdmin');
            if (storedAdminStatus !== null) {
                // 使用存储的管理员状态
                setIsAdmin(storedAdminStatus === 'true');
                setCheckingAdmin(false);
                return;
            }

            // 如果没有存储，调用接口检查
            setCheckingAdmin(true);
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/checkAdminByCompanyId`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                userMobile: userMobile
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const adminStatus = response.data && response.data.code === 200;
            setIsAdmin(adminStatus);
            // 存储管理员状态到localStorage
            setUserCachedData(userId, 'isAdmin', adminStatus.toString());
        } catch (error) {
            console.error('检查管理员身份失败，保持缓存状态:', error);
            // 保持当前状态，不设置为false
            // 这样即使接口调用失败，也不会丢失权限
        } finally {
            setCheckingAdmin(false);
        }
    };

    // 检查是否是司机
    const checkDriverStatus = async (userMobile, userId) => {
        try {
            // 首先检查localStorage中是否已经存储了司机状态
            const storedDriverStatus = getUserCachedData(userId, 'isDriver');
            if (storedDriverStatus !== null) {
                // 使用存储的司机状态
                setIsDriver(storedDriverStatus === 'true');
                setCheckingDriver(false);
                return;
            }

            // 如果没有存储，调用接口检查
            setCheckingDriver(true);
            const response = await axios.post(`${API_CONFIG.baseUrl}/api/v1/carApplication/checkDriverByCompanyId`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                userMobile: userMobile
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const driverStatus = response.data && response.data.code === 200;
            setIsDriver(driverStatus);
            // 存储司机状态到localStorage
            setUserCachedData(userId, 'isDriver', driverStatus.toString());
        } catch (error) {
            console.error('检查司机身份失败，保持缓存状态:', error);
            // 保持当前状态，不设置为false
            // 这样即使接口调用失败，也不会丢失权限
        } finally {
            setCheckingDriver(false);
        }
    };

    // 初始化用户信息
    const initUserInfo = async () => {
        try {
            setLoading(true);
            setError(null);

            // 首先检查localStorage中是否有用户信息
            const storedUserInfo = localStorage.getItem('userInfo');
            if (storedUserInfo) {
                try {
                    const parsedUserInfo = JSON.parse(storedUserInfo);
                    const userId = parsedUserInfo ? (parsedUserInfo.user_id || parsedUserInfo.open_id) : null;

                    // 如果有基本用户信息，先记录下来作为备选
                    if (parsedUserInfo && userId) {
                        // 检查缓存是否包含必要字段
                        const hasRequiredFields =
                            parsedUserInfo.department_name &&
                            parsedUserInfo.name &&
                            parsedUserInfo.mobile;

                        // 如果有access_token，尝试获取详细信息
                        if (parsedUserInfo.access_token) {
                            const userCacheKey = getUserCacheKey(userId, 'userInfo');

                            // 检查 token 是否有效
                            if (!checkAccessTokenValidity(parsedUserInfo.access_token)) {
                                console.log('检测到 access_token 可能已过期，尝试刷新...');
                                try {
                                    const newTokenData = await refreshUserAccessToken();
                                    if (newTokenData) {
                                        // 使用新的 token 获取用户信息
                                        const detailedUserInfo = await fetchUserDetailInfo(
                                            newTokenData.access_token,
                                            newTokenData.open_id
                                        );
                                        console.log('使用新 token 获取详细用户信息成功');
                                        // 更新缓存
                                        localStorage.setItem('userInfo', JSON.stringify(detailedUserInfo));
                                        setUserCachedData(userId, 'userInfo', detailedUserInfo);
                                        setUserInfo(detailedUserInfo);
                                        // 检查是否是管理员和司机
                                        if (detailedUserInfo.mobile) {
                                            const mobile = detailedUserInfo.mobile.replace(/^\+86/, '');
                                            checkAdminStatus(mobile, userId);
                                            checkDriverStatus(mobile, userId);
                                        }
                                        return;
                                    }
                                } catch (refreshError) {
                                    console.error('刷新 access_token 失败，尝试使用旧 token:', refreshError);
                                }
                            }

                            try {
                                // 先尝试获取详细信息（不管缓存是否过期）
                                const detailedUserInfo = await fetchUserDetailInfo(parsedUserInfo.access_token, parsedUserInfo.open_id);
                                console.log('获取详细用户信息成功');
                                // 更新缓存
                                localStorage.setItem('userInfo', JSON.stringify(detailedUserInfo));
                                setUserCachedData(userId, 'userInfo', detailedUserInfo);
                                setUserInfo(detailedUserInfo);
                                // 检查是否是管理员和司机
                                if (detailedUserInfo.mobile) {
                                    const mobile = detailedUserInfo.mobile.replace(/^\+86/, '');
                                    checkAdminStatus(mobile, userId);
                                    checkDriverStatus(mobile, userId);
                                }
                                return;
                            } catch (error) {
                                console.error('获取详细用户信息失败，尝试刷新 token:', error);

                                // 如果是 token 过期导致的错误，尝试刷新
                                if (error.response && (error.response.status === 401 || error.response.data?.code === 99991667)) {
                                    console.log('检测到 token 过期错误，尝试刷新...');
                                    try {
                                        const newTokenData = await refreshUserAccessToken();
                                        if (newTokenData) {
                                            const detailedUserInfo = await fetchUserDetailInfo(
                                                newTokenData.access_token,
                                                newTokenData.open_id
                                            );
                                            console.log('刷新 token 后获取用户信息成功');
                                            localStorage.setItem('userInfo', JSON.stringify(detailedUserInfo));
                                            setUserCachedData(userId, 'userInfo', detailedUserInfo);
                                            setUserInfo(detailedUserInfo);
                                            if (detailedUserInfo.mobile) {
                                                const mobile = detailedUserInfo.mobile.replace(/^\+86/, '');
                                                checkAdminStatus(mobile, userId);
                                                checkDriverStatus(mobile, userId);
                                            }
                                            return;
                                        }
                                    } catch (refreshError) {
                                        console.error('刷新 token 后仍然失败:', refreshError);
                                    }
                                }

                                // 使用缓存数据，不清除缓存
                                setUserInfo(parsedUserInfo);
                                // 检查是否是管理员和司机（从缓存读取）
                                if (parsedUserInfo.mobile) {
                                    const mobile = parsedUserInfo.mobile.replace(/^\+86/, '');
                                    checkAdminStatus(mobile, userId);
                                    checkDriverStatus(mobile, userId);
                                }
                                return;
                            }
                        } else {
                            // 没有access_token，只要有user_id就使用缓存
                            // 预约记录页面只需要user_id就能获取数据
                            console.log('使用缓存的用户信息（无access_token，仅需user_id）:', parsedUserInfo);
                            setUserInfo(parsedUserInfo);
                            // 检查是否是管理员和司机（如果有mobile）
                            if (parsedUserInfo.mobile) {
                                const mobile = parsedUserInfo.mobile.replace(/^\+86/, '');
                                checkAdminStatus(mobile, userId);
                                checkDriverStatus(mobile, userId);
                            }
                            return;
                        }
                    }

                    // 只有在解析失败或完全无效时才清除缓存
                } catch (parseError) {
                    console.error('解析localStorage中的用户信息失败:', parseError);
                    localStorage.removeItem('userInfo');
                }
            }


            // 飞书环境下获取用户信息
            const userInfoData = await new Promise((resolve) => {
                handleJSAPIAccess((isSuccess) => {
                    if (!isSuccess) {
                        console.log('JSAPI鉴权失败，尝试直接获取用户信息');
                        // 鉴权失败时也尝试获取用户信息
                        handleUserAuth((userInfoData) => {
                            resolve(userInfoData || null);
                        });
                    } else {
                        handleUserAuth((userInfoData) => {
                            if (userInfoData && userInfoData.access_token) {
                                const userId = userInfoData.user_id || userInfoData.open_id;
                                localStorage.setItem('userInfo', JSON.stringify(userInfoData));
                                setUserCachedData(userId, 'userInfo', userInfoData);
                                resolve(userInfoData);
                            } else {
                                resolve(null);
                            }
                        });
                    }
                });
            });

            if (userInfoData) {
                try {
                    const detailedUserInfo = await fetchUserDetailInfo(userInfoData.access_token, userInfoData.open_id);
                    // 更新缓存
                    const userId = userInfoData.user_id || userInfoData.open_id;
                    localStorage.setItem('userInfo', JSON.stringify(detailedUserInfo));
                    setUserCachedData(userId, 'userInfo', detailedUserInfo);

                    setUserInfo(detailedUserInfo);
                    // 检查是否是管理员和司机
                    if (detailedUserInfo.mobile) {
                        // 去掉手机号中的+86前缀
                        const mobile = detailedUserInfo.mobile.replace(/^\+86/, '');
                        checkAdminStatus(mobile, userId);
                        checkDriverStatus(mobile, userId);
                    }
                } catch (error) {
                    console.error('获取详细用户信息失败，保留现有部门信息:', error);
                    // 保留现有部门信息
                    if (userInfo && (userInfo.department_name || userInfo.department)) {
                        userInfoData.department_name = userInfo.department_name || userInfo.department;
                    }
                    setUserInfo(userInfoData);
                    // 检查是否是管理员和司机
                    if (userInfoData.mobile) {
                        // 去掉手机号中的+86前缀
                        const mobile = userInfoData.mobile.replace(/^\+86/, '');
                        const userId = userInfoData.user_id || userInfoData.open_id;
                        checkAdminStatus(mobile, userId);
                        checkDriverStatus(mobile, userId);
                    }
                }
            } else {
                // 未获取到用户信息，使用缓存或默认数据
                console.log('未获取到用户信息，尝试使用缓存数据');
                const cachedUserInfo = localStorage.getItem('userInfo');
                if (cachedUserInfo) {
                    try {
                        const parsed = JSON.parse(cachedUserInfo);
                        const userId = parsed.user_id || parsed.open_id;
                        if (userId) {
                            console.log('使用缓存的用户信息:', parsed);
                            setUserInfo(parsed);
                            if (parsed.mobile) {
                                const mobile = parsed.mobile.replace(/^\+86/, '');
                                checkAdminStatus(mobile, userId);
                                checkDriverStatus(mobile, userId);
                            }
                            return;
                        }
                    } catch (e) {
                        console.error('解析缓存用户信息失败:', e);
                    }
                }
                throw new Error('未获取到用户信息');
            }
        } catch (error) {
            console.error('初始化用户信息失败:', error);
            setError(error.message);
            // 使用默认数据
            setUserInfo({
                name: '未知用户',
                mobile: '',
                user_id: '',
                open_id: '',
                department: ''
            });
        } finally {
            setLoading(false);
        }
    };




    // 初始化时获取用户信息
    useEffect(() => {
        initUserInfo();
        
        // 设置定时检查 token 有效性（每30分钟检查一次）
        const tokenCheckInterval = setInterval(() => {
            console.log('定时检查：开始检查 access_token 状态...');
            
            const storedUserInfo = localStorage.getItem('userInfo');
            if (storedUserInfo) {
                try {
                    const userInfo = JSON.parse(storedUserInfo);
                    const userId = userInfo.user_id || userInfo.open_id;
                    
                    if (userInfo.access_token) {
                        // 检查 token 是否即将过期
                        if (!checkAccessTokenValidity(userInfo.access_token)) {
                            console.log('定时检查发现 token 需要刷新');
                            
                            // 执行刷新
                            refreshUserAccessToken()
                                .then((newTokenData) => {
                                    if (newTokenData) {
                                        console.log('定时刷新成功，更新用户信息');
                                        
                                        // 使用新 token 获取最新的用户详细信息
                                        return fetchUserDetailInfo(newTokenData.access_token, newTokenData.open_id);
                                    } else {
                                        throw new Error('刷新 token 失败');
                                    }
                                })
                                .then((detailedUserInfo) => {
                                    // 更新缓存和用户状态
                                    localStorage.setItem('userInfo', JSON.stringify(detailedUserInfo));
                                    setUserCachedData(userId, 'userInfo', detailedUserInfo);
                                    setUserInfo(detailedUserInfo);
                                    
                                    // 重新检查管理员和司机状态
                                    if (detailedUserInfo.mobile) {
                                        const mobile = detailedUserInfo.mobile.replace(/^\+86/, '');
                                        checkAdminStatus(mobile, userId);
                                        checkDriverStatus(mobile, userId);
                                    }
                                    
                                    console.log('定时刷新完成，token 已更新');
                                })
                                .catch((err) => {
                                    console.error('定时刷新 token 失败:', err);
                                    // 刷新失败不影响当前用户状态，继续使用旧数据
                                });
                        } else {
                            console.log('定时检查：token 仍然有效，无需刷新');
                        }
                    } else {
                        console.log('定时检查：无 access_token，跳过检查');
                    }
                } catch (error) {
                    console.error('定时检查 token 失败:', error);
                }
            } else {
                console.log('定时检查：无用户信息，跳过检查');
            }
        }, 30 * 60 * 1000); // 30分钟
        
        // 组件卸载时清除定时器
        return () => {
            console.log('清理定时检查任务');
            clearInterval(tokenCheckInterval);
        };
    }, []);

    return (
        <UserContext.Provider value={{ 
            userInfo, 
            loading, 
            error, 
            isAdmin, 
            isDriver, 
            checkingAdmin, 
            checkingDriver,
            refreshUserInfo: initUserInfo 
        }}>
            {children}
        </UserContext.Provider>
    );
};

// 自定义Hook，方便使用用户上下文
export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};